import React from 'react';
import { createLogo, createUPIQR, createSealOrSign } from '../../utils/imageUtils';
import { roundTo2Decimals, calculateGSTAmount } from '../../utils/numberFormat';
import { formatDateToCompanyFormat } from '../../utils/dateUtils';
import { calculateInvoiceDiscount } from './shared/billFormatUtils';

const PAGE_DIMENSIONS = {
  a4: { portrait: { width: 210, height: 297 }, landscape: { width: 297, height: 210 } },
  a5: { portrait: { width: 148, height: 210 }, landscape: { width: 210, height: 148 } },
};

function formatINR(num) {
  if (typeof num !== 'number') num = parseFloat(num);
  if (isNaN(num)) return '';
  return num.toLocaleString('en-IN');
}

function numberToWordsIndian(num) {
  const a = [ '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen' ];
  const b = [ '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety' ];
  let str = '';
  if (num >= 10000000) {
    str += numberToWordsIndian(Math.floor(num / 10000000)) + ' Crore ';
    num = num % 10000000;
  }
  if (num >= 100000) {
    str += numberToWordsIndian(Math.floor(num / 100000)) + ' Lakh ';
    num = num % 100000;
  }
  if (num >= 1000) {
    str += numberToWordsIndian(Math.floor(num / 1000)) + ' Thousand ';
    num = num % 1000;
  }
  if (num >= 100) {
    str += numberToWordsIndian(Math.floor(num / 100)) + ' Hundred ';
    num = num % 100;
  }
  if (num > 0) {
    if (str !== '') str += 'and ';
    if (num < 20) str += a[num] + ' ';
    else str += b[Math.floor(num / 10)] + (num % 10 ? ' ' + a[num % 10] : '') + ' ';
  }
  return str.replace(/ +/g, ' ').trim();
}

function numToWords(num) {
  if (typeof num !== 'number') num = parseInt(num, 10);
  if (isNaN(num)) return '';
  if (num === 0) return 'Zero Rupees Only';
  return numberToWordsIndian(num) + ' Rupees Only';
}

function ModernSalesTemplate({
  billData = {},
  companyDetails = {},
  partyDetails = {},
  bankDetails = {},
  pageSize = 'a4',
  orientation = 'portrait',
  previewMode = false,
  showPlaceholders = false,
}) {
  const visibleItems = (billData.items || []).filter(it => {
    const hasDesc = !!(it.description && String(it.description).trim());
    const hasItem = !!(it.item && String(it.item).trim());
    const hasNumbers = (parseFloat(it.qty) || 0) > 0 || (parseFloat(it.rate) || 0) > 0 || (parseFloat(it.amount) || 0) > 0 || (parseFloat(it.total) || 0) > 0;
    return hasDesc || hasItem || hasNumbers;
  });

  const {
    subtotal,
    discount,
    discountedSubtotal,
    discountRatio,
    grossAmount,
  } = calculateInvoiceDiscount(visibleItems, billData);
  const totalSGST = visibleItems.reduce((sum, item) => {
    const amt = parseFloat(item.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, item.sgst);
  }, 0) || 0;
  const totalCGST = visibleItems.reduce((sum, item) => {
    const amt = parseFloat(item.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, item.cgst);
  }, 0) || 0;
  const totalIGST = visibleItems.reduce((sum, item) => {
    const amt = parseFloat(item.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, item.igst || item.gst);
  }, 0) || 0;
  const grandTotal = roundTo2Decimals(discountedSubtotal + totalSGST + totalCGST + totalIGST);

  const pageDims = PAGE_DIMENSIONS[pageSize]?.[orientation] || PAGE_DIMENSIONS.a4.portrait;
  const isA5 = pageSize === 'a5';
  
  const pageStyle = {
    width: '100%',
    maxWidth: `${pageDims.width}mm`,
    minHeight: `${pageDims.height}mm`,
    margin: '0 auto',
    background: '#ffffff',
    boxSizing: 'border-box',
    padding: isA5 ? '8px' : '12px',
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <div style={pageStyle} className="print:p-0 print:shadow-none print:border-0" id="modern-sales-template">
      {/* Header with gradient accent */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: isA5 ? '8px 12px' : '12px 16px',
        borderRadius: '8px',
        marginBottom: '12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isA5 ? '16px' : '22px', fontWeight: 'bold' }}>
            {companyDetails.gstinType === 'Regular' && 'TAX INVOICE'}
            {companyDetails.gstinType === 'Composition' && 'BILL OF SUPPLY'}
            {companyDetails.gstinType === 'Unregistered' && 'INVOICE'}
          </h1>
          <div style={{ fontSize: isA5 ? '9px' : '11px', marginTop: '4px', opacity: 0.9 }}>
            No: {previewMode ? 'XXX' : billData.invoiceNumber} | 
            Date: {previewMode ? 'XXX' : formatDateToCompanyFormat(billData.date, companyDetails)}
          </div>
        </div>
        <div>
          {createLogo(companyDetails.logoUrl, companyDetails.firmName || 'Company', isA5 ? 'h-12 w-12' : 'h-16 w-16')}
        </div>
      </div>

      {/* Company & Party Section - Modern Cards */}
      <div className="flex gap-3 mb-3 modern-party-row" style={{ gap: '12px' }}>
        {/* Company Card */}
        <div style={{ 
          flex: 1, 
          padding: isA5 ? '8px' : '12px', 
          background: '#f8fafc',
          borderLeft: '4px solid #667eea',
          borderRadius: '6px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: isA5 ? '9px' : '10px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>FROM</div>
          <div style={{ fontSize: isA5 ? '10px' : '12px', fontWeight: 'bold', color: '#1e293b', marginBottom: '3px' }}>
            {companyDetails.name || companyDetails.firmName || '-'}
          </div>
          <div style={{ fontSize: isA5 ? '9px' : '10px', color: '#64748b', lineHeight: '1.4' }}>
            {companyDetails.address || '-'}
          </div>
          <div style={{ fontSize: isA5 ? '8px' : '9px', color: '#64748b', marginTop: '4px' }}>
            GST: {companyDetails.gstin || '-'} | Mob: {companyDetails.contact || companyDetails.contactNumber || '-'}
          </div>
        </div>

        {/* Party Card */}
        <div style={{ 
          flex: 1, 
          padding: isA5 ? '8px' : '12px', 
          background: '#f8fafc',
          borderLeft: '4px solid #10b981',
          borderRadius: '6px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: isA5 ? '9px' : '10px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>TO</div>
          <div style={{ fontSize: isA5 ? '10px' : '12px', fontWeight: 'bold', color: '#1e293b', marginBottom: '3px' }}>
            {previewMode ? 'XXX Name' : partyDetails.name}
          </div>
          <div style={{ fontSize: isA5 ? '9px' : '10px', color: '#64748b', lineHeight: '1.4' }}>
            {previewMode ? 'XXX Address' : partyDetails.address}
          </div>
          <div style={{ fontSize: isA5 ? '8px' : '9px', color: '#64748b', marginTop: '4px' }}>
            GST: {previewMode ? 'XXX' : (partyDetails.gstin || '-')} | Mob: {previewMode ? 'XXX' : (partyDetails.contact || '-')}
          </div>
        </div>
      </div>

      {/* Items Table - Modern Design */}
      <div className="overflow-x-auto mb-3">
        <table className="w-full" style={{ fontSize: isA5 ? '9px' : '10px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#667eea', color: 'white' }}>
              <th style={{ padding: '8px', textAlign: 'left', borderRadius: '6px 0 0 0' }}>#</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Description</th>
              {companyDetails.gstinType === 'Regular' && <th style={{ padding: '8px', textAlign: 'center' }}>HSN</th>}
              <th style={{ padding: '8px', textAlign: 'center' }}>Qty</th>
              <th style={{ padding: '8px', textAlign: 'right' }}>Rate</th>
              {companyDetails.gstinType === 'Regular' && <th style={{ padding: '8px', textAlign: 'center' }}>GST%</th>}
              <th style={{ padding: '8px', textAlign: 'right', borderRadius: '0 6px 0 0' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item, idx) => (
              <tr key={idx} style={{ 
                background: idx % 2 === 0 ? '#ffffff' : '#f8fafc',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <td style={{ padding: '8px', fontWeight: '500' }}>{idx + 1}</td>
                <td style={{ padding: '8px' }}>
                  <div style={{ fontWeight: '500' }}>{item.description}</div>
                  {item.qtyDisplay && (
                    <div style={{ fontSize: '0.9em', color: '#64748b', marginTop: '2px' }}>{item.qtyDisplay}</div>
                  )}
                </td>
                {companyDetails.gstinType === 'Regular' && (
                  <td style={{ padding: '8px', textAlign: 'center', color: '#64748b' }}>{item.hsn}</td>
                )}
                <td style={{ padding: '8px', textAlign: 'center', color: '#64748b' }}>
                  {item.qty} {item.unit || item.quantityMeasurement || 'Nos'}
                </td>
                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500' }}>₹{item.rate}</td>
                {companyDetails.gstinType === 'Regular' && (
                  <td style={{ padding: '8px', textAlign: 'center', color: '#64748b' }}>
                    {item.gst || (parseFloat(item.sgst || 0) + parseFloat(item.cgst || 0) + parseFloat(item.igst || 0))}%
                  </td>
                )}
                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#1e293b' }}>
                  ₹{item.total !== undefined && item.total !== null && item.total !== "" ? item.total : item.amount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals & Payment - Side by Side */}
      <div className="flex gap-3 mb-3 modern-payment-row">
        {/* Payment Details */}
        <div style={{ 
          flex: 1, 
          padding: isA5 ? '8px' : '12px',
          background: '#f1f5f9',
          borderRadius: '6px'
        }}>
          <div style={{ fontSize: isA5 ? '9px' : '10px', fontWeight: 'bold', color: '#667eea', marginBottom: '6px' }}>
            Bank & Payment Details
          </div>
          {companyDetails.bankName && (
            <div style={{ fontSize: isA5 ? '9px' : '10px', marginBottom: '4px', fontWeight: '500' }}>
              {companyDetails.bankName}
            </div>
          )}
          {companyDetails.bankAccount && (
            <div style={{ fontSize: isA5 ? '8px' : '9px', color: '#64748b', marginBottom: '2px' }}>
              A/C: {companyDetails.bankAccount}
            </div>
          )}
          {companyDetails.bankIfsc && (
            <div style={{ fontSize: isA5 ? '8px' : '9px', color: '#64748b', marginBottom: '2px' }}>
              IFSC: {companyDetails.bankIfsc}
            </div>
          )}
          {companyDetails.upiId && (
            <div style={{ fontSize: isA5 ? '8px' : '9px', color: '#64748b', marginBottom: '2px' }}>
              UPI: {companyDetails.upiId}
            </div>
          )}
          {companyDetails.upiQrUrl && (
            <div style={{ marginTop: '8px', textAlign: 'center' }}>
              {createUPIQR(companyDetails.upiQrUrl, isA5 ? 'w-14 h-14' : 'w-16 h-16')}
            </div>
          )}
        </div>

        {/* Summary */}
        <div style={{ 
          flex: 1, 
          padding: isA5 ? '8px' : '12px',
          background: '#fef3c7',
          borderRadius: '6px'
        }}>
          <div style={{ fontSize: isA5 ? '9px' : '10px', fontWeight: 'bold', color: '#f59e0b', marginBottom: '6px' }}>
            Summary
          </div>
          {companyDetails.gstinType === 'Regular' ? (
            <>
              <div style={{ fontSize: isA5 ? '9px' : '10px', marginBottom: '3px' }}>
                Subtotal: ₹{previewMode ? 'XXX' : subtotal}
              </div>
              {discount > 0 && (
                <div style={{ fontSize: isA5 ? '9px' : '10px', marginBottom: '3px', color: '#10b981' }}>
                  Discount: -₹{previewMode ? 'XXX' : discount}
                </div>
              )}
              <div style={{ fontSize: isA5 ? '9px' : '10px', marginBottom: '3px' }}>
                SGST: ₹{previewMode ? 'XXX' : totalSGST}
              </div>
              <div style={{ fontSize: isA5 ? '9px' : '10px', marginBottom: '3px' }}>
                CGST: ₹{previewMode ? 'XXX' : totalCGST}
              </div>
              <div style={{ fontSize: isA5 ? '9px' : '10px', marginBottom: '3px' }}>
                IGST: ₹{previewMode ? 'XXX' : totalIGST}
              </div>
              <div style={{ 
                fontSize: isA5 ? '11px' : '13px', 
                fontWeight: 'bold', 
                color: '#1e293b', 
                marginTop: '6px',
                paddingTop: '6px',
                borderTop: '2px solid #fbbf24'
              }}>
                Total: ₹{previewMode ? 'XXX' : grandTotal}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: isA5 ? '9px' : '10px', marginBottom: '3px' }}>
                Amount: ₹{previewMode ? 'XXX' : grossAmount}
              </div>
              {discount > 0 && (
                <div style={{ fontSize: isA5 ? '9px' : '10px', marginBottom: '3px', color: '#10b981' }}>
                  Discount: -₹{previewMode ? 'XXX' : discount}
                </div>
              )}
              <div style={{ 
                fontSize: isA5 ? '11px' : '13px', 
                fontWeight: 'bold', 
                color: '#1e293b', 
                marginTop: '6px',
                paddingTop: '6px',
                borderTop: '2px solid #fbbf24'
              }}>
                Total: ₹{previewMode ? 'XXX' : discountedSubtotal}
              </div>
            </>
          )}
          <div style={{ 
            fontSize: isA5 ? '8px' : '9px', 
            marginTop: '6px', 
            color: '#64748b', 
            fontStyle: 'italic',
            lineHeight: '1.3'
          }}>
            {numToWords(roundTo2Decimals(grandTotal))}
          </div>
        </div>
      </div>

      {String(billData.notes || '').trim() && (
        <div style={{ 
          fontSize: isA5 ? '8px' : '9px', 
          padding: isA5 ? '8px' : '10px',
          marginBottom: isA5 ? '20px' : '28px',
          background: '#eff6ff',
          borderLeft: '4px solid #3b82f6',
          borderRadius: '6px',
          color: '#1e3a8a',
          lineHeight: '1.5'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: isA5 ? '9px' : '10px' }}>
            Note:
          </div>
          <div style={{ whiteSpace: 'pre-line' }}>
            {previewMode ? 'XXXX' : String(billData.notes).trim()}
          </div>
        </div>
      )}

      {/* Terms and Conditions */}
      {(companyDetails.salesTerms || companyDetails.terms) && (
        <div style={{ 
          fontSize: isA5 ? '8px' : '9px', 
          padding: isA5 ? '8px' : '10px',
          marginBottom: '6px',
          background: '#ecfdf5',
          borderLeft: '4px solid #10b981',
          borderRadius: '6px',
          color: '#065f46',
          lineHeight: '1.5'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: isA5 ? '9px' : '10px' }}>
            Terms & Conditions:
          </div>
          <div style={{ whiteSpace: 'pre-line' }}>
            {companyDetails.salesTerms || companyDetails.terms}
          </div>
        </div>
      )}

      {companyDetails.gstinType === 'Composition' && (
        <div style={{ 
          fontSize: isA5 ? '8px' : '9px', 
          color: '#dc2626', 
          fontWeight: 'bold', 
          padding: '6px',
          background: '#fef2f2',
          borderRadius: '4px',
          marginBottom: '6px'
        }}>
          Composition taxable person - Not eligible to collect tax
        </div>
      )}

      {/* Signatures */}
      <div className="flex justify-between modern-sign-row" style={{ marginTop: '28px', paddingTop: '32px', minHeight: '80px', fontSize: isA5 ? '9px' : '10px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            borderTop: '2px solid #1e293b', 
            width: '100px', 
            margin: '0 auto 6px',
            paddingTop: '4px'
          }}>
            <div style={{ color: '#64748b' }}>Receiver's Signature</div>
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'center', 
            marginBottom: '6px' 
          }}>
            {bankDetails.sealUrl && createSealOrSign(bankDetails.sealUrl, 'Seal', isA5 ? 'h-8 w-8' : 'h-10 w-10')}
            {bankDetails.signUrl && createSealOrSign(bankDetails.signUrl, 'Sign', isA5 ? 'h-8 w-16' : 'h-10 w-20')}
          </div>
          <div style={{ color: '#64748b' }}>Company Sign & Seal</div>
        </div>
      </div>

      <style>{`
        @media screen and (max-width: 640px) {
          .modern-party-row,
          .modern-payment-row,
          .modern-sign-row {
            flex-direction: column !important;
            gap: 8px !important;
          }
        }

        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          @page {
            size: ${pageDims.width}mm ${pageDims.height}mm ${orientation};
            margin: 6mm;
          }
          
          .print\\:p-0 { padding: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-0 { border: none !important; }
          
          img {
            display: block !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            visibility: visible !important;
          }
        }
      `}</style>
    </div>
  );
}

export default ModernSalesTemplate;
